import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { usePost, usePatch } from "../../utils/apiComponent/useApi"
import { api } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"
import { toastUtils } from "../../utils/toast"

export default function UserFormModal({ isOpen, onClose, user = null, onSuccess, defaultRole = "student" }) {
  const [generatedPassword, setGeneratedPassword] = useState("")
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: defaultRole,
    phone: "",
    address: "",
    city: "",
    country: "",
  })

  // Réinitialiser le formulaire quand le modal s'ouvre ou que l'utilisateur change
  useEffect(() => {
    if (isOpen) {
      if (user) {
        // Mode édition : remplir avec les données de l'utilisateur
        const nameParts = user.name ? String(user.name).split(" ") : []
        const firstName = nameParts[0] || ""
        const lastName = nameParts.slice(1).join(" ") || ""
        
        // Normaliser le rôle pour l'affichage (convertir STUDENT en student)
        const userRole = user.role ? user.role.toString().toLowerCase() : defaultRole
        
        setFormData({
          firstName,
          lastName,
          email: user.email || "",
          password: "",
          role: userRole === 'student' || userRole === 'admin' ? userRole : defaultRole,
          phone: user.phone || "",
          address: user.address || "",
          city: user.city || "",
          country: user.country || "",
        })
        console.log('[UserFormModal] Mode édition - Données utilisateur:', user)
        console.log('[UserFormModal] Formulaire initialisé avec:', { firstName, lastName, email: user.email, role: user.role })
      } else {
        // Mode création : formulaire vide
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          role: defaultRole,
          phone: "",
          address: "",
          city: "",
          country: "",
        })
        console.log('[UserFormModal] Mode création - Formulaire réinitialisé')
      }
      setGeneratedPassword("")
      setErrors({})
    }
  }, [isOpen, user, defaultRole])

  const isEditMode = !!user
  
  const { execute: createExecute, loading: creating } = usePost(endpoints.register, { immediate: false })
  const [updating, setUpdating] = useState(false)

  const loading = creating || updating

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*"
    let password = ""
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setGeneratedPassword(password)
    setFormData(prev => ({ ...prev, password }))
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Le prénom est requis"
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Le nom est requis"
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format d'email invalide"
    }

    // Le mot de passe est requis uniquement pour la création
    if (!isEditMode && !formData.password && !generatedPassword) {
      newErrors.password = "Le mot de passe est requis"
    } else if (!isEditMode && formData.password && formData.password.length < 6) {
      newErrors.password = "Le mot de passe doit contenir au moins 6 caractères"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      console.log('[UserFormModal] Validation échouée, erreurs:', errors)
      return
    }

    try {
      if (isEditMode) {
        // Mode modification
        if (!user?.id) {
          toastUtils.error("ID utilisateur manquant")
          return
        }
        
        setUpdating(true)
        // Construire le payload avec tous les champs modifiables
        const payload = {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
        }
        
        // Ne pas envoyer password si vide (laisse le backend décider)
        if (formData.password && formData.password.trim()) {
          payload.password = formData.password.trim()
        }
        
        // Inclure les autres champs (même s'ils sont vides, pour permettre de les effacer)
        // Note: Certains backends peuvent ignorer les champs vides, mais on les envoie quand même
        payload.phone = formData.phone?.trim() || null
        payload.city = formData.city?.trim() || null
        payload.country = formData.country?.trim() || null
        payload.address = formData.address?.trim() || null
        
        console.log('[UserFormModal] Modification - Payload:', payload)
        console.log('[UserFormModal] Modification - Endpoint:', endpoints.updateUser(user.id))
        console.log('[UserFormModal] Modification - User ID:', user.id)
        
        try {
          const response = await api.patch(endpoints.updateUser(user.id), payload)
          console.log('[UserFormModal] Modification réussie, response:', response)
          console.log('[UserFormModal] Données avant modification:', user)
          console.log('[UserFormModal] Données après modification:', response)
          
          toastUtils.success("Utilisateur modifié avec succès")
          
          // Attendre un peu avant de fermer pour que le toast s'affiche
          await new Promise(resolve => setTimeout(resolve, 300))
          
          onSuccess?.()
          onClose()
        } catch (error) {
          console.error('[UserFormModal] Erreur lors de la modification:', error)
          throw error // Re-lancer l'erreur pour qu'elle soit gérée par le catch principal
        } finally {
          setUpdating(false)
        }
      } else {
        // Mode création
        const name = `${formData.firstName} ${formData.lastName}`.trim()
        const finalPassword = generatedPassword || formData.password
        
        const registerBody = {
          name,
          email: formData.email,
          password: finalPassword,
          confirmPassword: finalPassword,
          role: formData.role?.toUpperCase?.() || 'STUDENT',
          ...(formData.phone && { phone: formData.phone }),
          ...(formData.address && { address: formData.address }),
          ...(formData.city && { city: formData.city }),
          ...(formData.country && { country: formData.country }),
        }
        
        console.log('[UserFormModal] Création - Payload:', { ...registerBody, password: '***', confirmPassword: '***' })
        console.log('[UserFormModal] Création - Endpoint:', endpoints.register)
        
        try {
          // Tentative via /users (plus simple) d'abord
          const usersBody = { 
            name, 
            email: formData.email, 
            password: finalPassword,
            role: formData.role?.toUpperCase?.() || 'STUDENT',
            ...(formData.phone && formData.phone.trim() && { phone: formData.phone.trim() }),
            ...(formData.address && formData.address.trim() && { address: formData.address.trim() }),
            ...(formData.city && formData.city.trim() && { city: formData.city.trim() }),
            ...(formData.country && formData.country.trim() && { country: formData.country.trim() }),
          }
          console.log('[UserFormModal] Tentative création via /users', { ...usersBody, password: '***' })
          try {
            await api.post(endpoints.createUser, usersBody)
            console.log('[UserFormModal] Création réussie via /users')
          } catch (usersErr) {
            console.log('[UserFormModal] Échec via /users (status:', usersErr.status, '), tentative via /auth/register:', usersErr.message)
            
            // Si l'erreur est due à un email existant, ne pas essayer /auth/register
            if (usersErr.message?.toLowerCase().includes('email') || 
                usersErr.message?.toLowerCase().includes('déjà') ||
                usersErr.message?.toLowerCase().includes('already') ||
                usersErr.status === 409 || usersErr.status === 422) {
              throw usersErr // Relancer l'erreur pour qu'elle soit gérée en dessous
            }
            
            // Fallback via /auth/register (inscription + vérification email)
            await createExecute(registerBody)
            console.log('[UserFormModal] Création réussie via /auth/register')
          }
          
          toastUtils.success("Utilisateur créé avec succès")
          onSuccess?.()
          onClose()
        } catch (createError) {
          // Si l'erreur vient du fallback, la relancer
          throw createError
        }
      }
    } catch (error) {
      console.error('[UserFormModal] Erreur lors de l\'opération:', error)
      console.error('[UserFormModal] Détails de l\'erreur:', {
        message: error.message,
        status: error.status,
        details: error.details
      })
      
      // Afficher un message d'erreur plus détaillé
      let errorMessage = "Une erreur est survenue"
      if (error.message) {
        errorMessage = error.message
      } else if (error.details?.message) {
        errorMessage = error.details.message
      } else if (error.details?.errors && Array.isArray(error.details.errors)) {
        errorMessage = error.details.errors.join(', ')
      }
      
      // Si l'erreur concerne un email déjà utilisé, donner plus de contexte
      if (errorMessage.toLowerCase().includes('email') || 
          errorMessage.toLowerCase().includes('déjà') ||
          errorMessage.toLowerCase().includes('already') ||
          errorMessage.toLowerCase().includes('unique') ||
          error.status === 409 || error.status === 422) {
        errorMessage = `Cette adresse email est déjà utilisée. 
        
⚠️ Note importante : Si vous avez supprimé cet utilisateur récemment, le backend utilise probablement une "suppression logicielle" (soft delete). L'utilisateur n'est pas vraiment supprimé de la base de données, juste marqué comme supprimé. L'email reste donc bloqué par la contrainte d'unicité.

Solutions :
1. Utilisez une autre adresse email pour créer le nouvel utilisateur
2. Contactez l'équipe backend pour corriger la contrainte d'unicité afin qu'elle ignore les utilisateurs soft deleted
3. Demandez une suppression définitive (hard delete) de l'utilisateur dans la base de données`
      }
      
      toastUtils.error(errorMessage)
      
      // Afficher les erreurs de validation dans le formulaire
      if (error.details?.errors && Array.isArray(error.details.errors)) {
        const validationErrors = {}
        error.details.errors.forEach(err => {
          if (err.field) {
            validationErrors[err.field] = err.message
          }
        })
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors)
        }
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-transparent flex items-center justify-center z-50 p-4" style={{backdropFilter: 'blur(8px)'}}>
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditMode ? "Modifier l'utilisateur" : (defaultRole === 'admin' ? "Créer un nouvel admin" : "Créer un nouvel étudiant")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations personnelles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.firstName ? "border-red-500" : ""
                  }`}
                  placeholder="Prénom"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.lastName ? "border-red-500" : ""
                  }`}
                  placeholder="Nom"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                errors.email ? "border-red-500" : ""
              }`}
              placeholder="email@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Rôle - seulement en mode création */}
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                  errors.role ? "border-red-500" : ""
                }`}
              >
                <option value="student">Étudiant</option>
                <option value="admin">Administrateur</option>
              </select>
              {errors.role && (
                <p className="text-red-500 text-sm mt-1">{errors.role}</p>
              )}
            </div>
          )}
          
          {/* Affichage du rôle en mode édition (non modifiable) */}
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle (non modifiable)
              </label>
              <input
                type="text"
                value={formData.role === 'student' ? 'Étudiant' : formData.role === 'admin' ? 'Administrateur' : formData.role}
                readOnly
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Le rôle ne peut pas être modifié après la création</p>
            </div>
          )}

          {/* Téléphone et Ville face à face */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder="+33 6 12 34 56 78"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ville
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder="Paris"
              />
            </div>
          </div>

          {/* Pays */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pays
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
              placeholder="France"
            />
          </div>

          {/* Génération de mot de passe */}
          {!isEditMode && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Mot de passe</h3>
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Générer un mot de passe
                </button>
                {generatedPassword && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mot de passe généré
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={generatedPassword}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(generatedPassword)}
                        className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Copier
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-2">{errors.password}</p>
              )}
              <p className="text-sm text-gray-500 mt-2">
                ⚠️ Copiez ce mot de passe avant de valider, il ne sera pas envoyé par email.
              </p>
            </div>
          )}

          {/* Mot de passe pour édition */}
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouveau mot de passe (laisser vide pour ne pas changer)
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                  errors.password ? "border-red-500" : ""
                }`}
                placeholder="********"
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#203a8a] text-white rounded-lg hover:bg-[#0f1220] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Chargement..." : isEditMode ? "Modifier" : "Créer l'utilisateur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

